import * as React from 'react';

import { enumUnit, Unit } from '@rematter/frontend-lib-api-client';
import { enumSalesLoadLineItemPhase } from '@rematter/frontend-lib-api-client';
import { Button, convertFromPounds } from '@rematter/ui-web';
import {
  humanReadableCurrency,
  humanReadableWeight,
  humanReadableShortPricingUnit,
  weightToPounds,
  cn,
} from '@rematter/ui-web/lib';
import { useAtomValue, useSetAtom } from 'jotai';
import groupBy from 'lodash/groupBy';
import { LucideArrowRight } from 'lucide-react';
import { Label } from 'react-aria-components';

import { useLineItemPhases } from '../../../hooks/useLineItemPhases';

import { gridLayoutMaterialReconcilitation } from './constants/gridLayout';
import { ReceivedDeductionLineItemForm } from './ReceivedDeductionLineItem';
import { ReceivedLineItemForm } from './ReceivedLineItem';

import { $inventoryItems } from '~/modules/trade/_state/loadForm.state';
import {
  $addReceivedMaterialItem,
  $getDeductionsForReceivedLineItem,
  $getReceivedItemsForLoadLineItem,
} from '~/modules/trade/_state/loadReconciliation.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { DeductionTypeRecord } from '~/modules/trade/types/deductionType';
import {
  enumLoadWeightType,
  LoadLineItemRecord,
  WeightUnitType,
} from '~/modules/trade/types/load';
import { convertWeightForPricing } from '~/modules/trade/utils/weight';
import { getLocale } from '~/shared/utils/locale';

interface ReconciliationItemCardProps {
  itemId: string;
  lineItems: LoadLineItemRecord[];
  deductionTypes: DeductionTypeRecord[];
  dialogMode?: boolean;
  disabled?: boolean;
  weightUnit?: WeightUnitType;
  facilityDefaultUnit?: Unit;
}

// Footer cell style (matches LoadDetailFooter)
export type FooterCellProps = {
  label: string;
  value: number;
  suffix?: string;
  type?: 'currency' | 'weight';
};

export const ReconciliationItemCard = ({
  itemId,
  lineItems,
  deductionTypes,
  dialogMode = true,
  disabled = false,
  weightUnit = 'price',
  facilityDefaultUnit = enumUnit.Pounds,
}: ReconciliationItemCardProps) => {
  const t = useTranslations();

  const { lineItemsByPhase: shippedLineItems } = useLineItemPhases({
    lineItems,
    loadPhaseType: enumSalesLoadLineItemPhase.Shipped,
  });

  const getReceivedItems = useAtomValue($getReceivedItemsForLoadLineItem);
  const getDeductionsByReceivedId = useAtomValue(
    $getDeductionsForReceivedLineItem,
  );

  const addReceivedMaterial = useSetAtom($addReceivedMaterialItem);

  // Determine display unit based on weight unit toggle and pricing unit
  const getDisplayUnit = React.useCallback(
    (pricingUnit: Unit | null | undefined): Unit => {
      const currentPricingUnit = pricingUnit || enumUnit.Pounds;

      // Exception: "Each" always displays as "Each" regardless of toggle
      if (currentPricingUnit === enumUnit.Each) return enumUnit.Each;

      // Otherwise respect the toggle: 'price' uses pricing unit, 'scale' uses facility default
      return weightUnit === 'price' ? currentPricingUnit : facilityDefaultUnit;
    },
    [weightUnit, facilityDefaultUnit],
  );

  const { shippedNet, shippedPrice, shippedTotal, shippedDisplayUnit } =
    React.useMemo(() => {
      // Get pricing unit from first line item
      const pricingUnit =
        (shippedLineItems[0]?.pricingUnit as Unit) || enumUnit.Pounds;

      const displayUnit = getDisplayUnit(pricingUnit);

      // Calculate shipped net in pounds first, then convert to display unit
      const shippedNetPounds = shippedLineItems.reduce(
        (sum, li) => sum + weightToPounds(li.weights?.[enumLoadWeightType.Net]),
        0,
      );

      const shippedNet = convertFromPounds(shippedNetPounds, displayUnit);

      const shippedPrice = shippedLineItems[0]?.unitPrice?.amount ?? 0;

      // Calculate shipped total using unit price and converted weight to respect pricing unit
      const shippedTotal = shippedLineItems.reduce((sum, li) => {
        const netWeightPounds = weightToPounds(
          li.weights?.[enumLoadWeightType.Net],
        );
        const pricingUnit = li.pricingUnit ?? enumUnit.Pounds;
        const convertedWeight = convertWeightForPricing(
          netWeightPounds,
          pricingUnit,
        );
        return sum + (li.unitPrice?.amount ?? 0) * convertedWeight;
      }, 0);

      return {
        shippedNet,
        shippedPrice,
        shippedTotal,
        shippedDisplayUnit: displayUnit,
      };
    }, [shippedLineItems, getDisplayUnit]);

  const receivedItems = lineItems.flatMap((li) => getReceivedItems(li.id));

  const handleAddReceivedMaterial = () => {
    // For now, associate with the first line item of this material
    if (lineItems.length > 0) {
      addReceivedMaterial(lineItems[0].id, { lineItems });
    }
  };

  const { data: itemsData } = useAtomValue($inventoryItems);
  const itemOptions = React.useMemo(
    () =>
      itemsData?.allItems.map((item) => ({
        id: item.id,
        label: `${item.mnemonic} - ${item.name}`,
        mnemonic: item.mnemonic ?? '',
        group:
          item.itemGroup?.name?.length && item.itemGroup?.name?.length > 0
            ? item.itemGroup?.name
            : 'N/A',
      })),
    [itemsData],
  );

  const currentItem = React.useMemo(
    () => itemsData?.itemsById[itemId],
    [itemsData, itemId],
  );

  const deductionsByReceivedId = React.useMemo(() => {
    const allDeductions = receivedItems.flatMap((ri) =>
      getDeductionsByReceivedId(ri.id),
    );

    return groupBy(allDeductions, 'receivedLineItemId');
  }, [receivedItems, getDeductionsByReceivedId]);

  const lineItemOptions = React.useMemo(
    () =>
      receivedItems.map((item) => {
        // Find the corresponding item in itemOptions by itemId
        const matchingItem = itemOptions?.find(
          (option) => option.id === item.itemId,
        );
        return {
          id: item.id,
          label: matchingItem?.label || item.materialType || `Item ${item.id}`,
        };
      }),
    [receivedItems, itemOptions],
  );

  // Calculate received net and total value
  const { receivedNet, receivedTotal } = React.useMemo(() => {
    // Calculate received net in pounds first, then convert to display unit
    const receivedNetPounds = receivedItems.reduce(
      (sum, item) =>
        sum +
        (weightToPounds(item.grossWeight) - weightToPounds(item.tareWeight)),
      0,
    );
    const receivedNet = convertFromPounds(
      receivedNetPounds,
      shippedDisplayUnit,
    );

    // Calculate price total using proper unit conversion for pricing
    const receivedTotal = receivedItems.reduce((sum, item) => {
      const netWeightPounds =
        weightToPounds(item.grossWeight) - weightToPounds(item.tareWeight);
      const pricingUnit = item.pricingUnit ?? enumUnit.Pounds;
      const convertedWeight = convertWeightForPricing(
        netWeightPounds,
        pricingUnit,
      );
      return sum + (item.unitPrice?.amount ?? 0) * convertedWeight;
    }, 0);

    return { receivedNet, receivedTotal };
  }, [receivedItems, shippedDisplayUnit]);

  const netDelta = receivedNet - shippedNet;
  const priceDelta = receivedTotal - shippedTotal;

  // Only show footer if there's a meaningful difference:
  // - At least $0.01 for price delta (one cent)
  // - At least 0.01 for weight delta (accounts for display rounding)
  const showFooter =
    receivedItems.length > 0 &&
    (Math.abs(netDelta) >= 0.01 || Math.abs(priceDelta) >= 0.01);

  const FooterCell = ({
    label,
    value,
    suffix,
    type = 'currency',
  }: FooterCellProps) => (
    <div className="@FooterCell flex flex-col items-end gap-1 py-1.5 *:leading-none">
      <div className="text-xs text-neutral/75 uppercase">{label}</div>
      <div className="flex flex-row items-end gap-2">
        <Label className="text-base leading-none font-bold text-neutral">
          {type === 'currency'
            ? humanReadableCurrency(value, {
                locale: getLocale(),
                maximumFractionDigits: 2,
              })
            : humanReadableWeight(value, {
                decimalPlaces: 2,
                includeUnit: false,
                locale: getLocale(),
              })}{' '}
          {type === 'weight'
            ? humanReadableShortPricingUnit(shippedDisplayUnit)
            : null}
        </Label>
        {suffix && <div className="text-xs text-neutral">{suffix}</div>}
      </div>
    </div>
  );

  return (
    <div className="@ReconciliationItemCard space-y-4 border-b border-neutral/20 bg-white py-4">
      <header>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-neutral/80">
              #{currentItem?.mnemonic} - {currentItem?.name}
            </h3>
            <div className="flex flex-row items-center gap-1 text-sm text-neutral/75">
              <p>{t.loadStatusTransition.reconciliation.shipped}</p>
              <LucideArrowRight className="text-neutral/80" size={12} />
              <p>
                N{' '}
                {humanReadableWeight(shippedNet, {
                  decimalPlaces: 2,
                  includeUnit: false,
                  convert: false,
                  locale: getLocale(),
                })}{' '}
                {humanReadableShortPricingUnit(shippedDisplayUnit)} &bull;{' '}
                {humanReadableCurrency(
                  typeof shippedPrice === 'object'
                    ? (shippedPrice ?? 0)
                    : (shippedPrice ?? 0),
                  { locale: getLocale() },
                )}{' '}
                &bull;{' '}
                {humanReadableCurrency(shippedTotal, { locale: getLocale() })}
              </p>
            </div>
          </div>
          {!disabled && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onPress={handleAddReceivedMaterial}
                isDisabled={disabled}
              >
                {
                  t.loadStatusTransition.reconciliation.actions
                    .addReceivedMaterial
                }
              </Button>
            </div>
          )}
        </div>
      </header>

      <div className="space-y-4">
        {receivedItems.length > 0 ? (
          receivedItems.map((receivedItem) => (
            <div key={receivedItem.id}>
              <ReceivedLineItemForm
                receivedItem={receivedItem}
                itemOptions={itemOptions ?? []}
                dialogMode={dialogMode}
                disabled={disabled}
                weightUnit={weightUnit}
                facilityDefaultUnit={facilityDefaultUnit}
              />
              {deductionsByReceivedId[receivedItem.id]?.map((deduction) => (
                <ReceivedDeductionLineItemForm
                  key={deduction.id}
                  deduction={deduction}
                  deductionTypes={deductionTypes ?? []}
                  lineItemOptions={lineItemOptions}
                  hideSecondInput={lineItemOptions.length <= 1}
                  dialogMode={dialogMode}
                  disabled={disabled}
                />
              ))}
            </div>
          ))
        ) : (
          <div className="text-left text-neutral/60">
            {t.materialsReconciliation.reconciliationItemCard.emptyState}
          </div>
        )}
      </div>
      {/* Footer row for net delta and price delta */}
      {showFooter && (
        <div
          className={cn(
            'grid w-full items-center gap-2',
            gridLayoutMaterialReconcilitation,
          )}
        >
          {/* Column 1 - 5: Empty */}
          <div />
          <div />
          <div />
          <div />
          <div />
          {/* Column 6: Net Delta */}
          <div>
            <FooterCell label="Net Delta" value={netDelta} type="weight" />
          </div>
          {/* Column 7: Price Delta */}
          <div>
            <FooterCell
              label="Price Delta"
              value={priceDelta}
              type="currency"
            />
          </div>
          {/* Column 8-10: Actions Button */}
          <div />
        </div>
      )}
    </div>
  );
};
